// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useState } from 'react';
import WorkspaceService from '../../../../../services/workspace/WorkspaceService';
import { useDatasets } from '../../../../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../../../../state/hooks/OrganizationHooks';
import {
  useCurrentScenarioId,
  useCurrentScenarioDatasetList,
  useCurrentScenarioParametersValues,
} from '../../../../../state/hooks/ScenarioHooks';
import { useWorkspaceId } from '../../../../../state/hooks/WorkspaceHooks';
import { useGestionActivites } from '../../../../../state/wizard/WizardHooks';
import { DatasetsUtils } from '../../../../../utils';

export const useActionsImposees = () => {
  const scenarioId = useCurrentScenarioId();
  const currentScenarioParametersValues = useCurrentScenarioParametersValues();
  const datasetId = useCurrentScenarioDatasetList()[0];
  const workspaceId = useWorkspaceId();
  const datasets = useDatasets();
  const organizationId = useOrganizationId();
  const gestionActivites = useGestionActivites(scenarioId);
  const [isLoading, setIsLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const SEQUENCE_ACTIONS_IMPOSEES = 'SequencesActionsImposees';

  const forgeNewActionsImposeesId = useCallback((gisement) => {
    return `${gisement}_${SEQUENCE_ACTIONS_IMPOSEES}`;
  }, []);

  const getActionsImposeesTableId = useCallback(
    (gisement) => {
      const actionsImposeesId = gestionActivites.find(
        (gestion) => gestion.Gisement === gisement
      )?.SequencesActionsImposees;
      return ![undefined, null, ''.trim()].includes(actionsImposeesId) ? actionsImposeesId : null;
    },
    [gestionActivites]
  );

  const getActionImposeesId = useCallback(
    (gisement) => {
      const actionImposeesTableId = getActionsImposeesTableId(gisement);
      return actionImposeesTableId ?? forgeNewActionsImposeesId(gisement);
    },
    [forgeNewActionsImposeesId, getActionsImposeesTableId]
  );

  const getActionImposeesDatasetId = useCallback(
    (gisement) => {
      const actionImposeesId = getActionsImposeesTableId(gisement);
      const unparsedTechnicalPoliciesParameters = currentScenarioParametersValues?.find(
        (parameter) => parameter.parameterId === 'TechnicalPoliciesWizard'
      );
      if (unparsedTechnicalPoliciesParameters == null) return null;

      try {
        const parsedTechnicalPoliciesParameters = JSON.parse(unparsedTechnicalPoliciesParameters.value);
        return parsedTechnicalPoliciesParameters?.find((parameter) => parameter.parameterId === actionImposeesId)
          ?.value;
      } catch (error) {
        console.error('Technical policies parameter values parsing failed', error);
        return null;
      }
    },
    [currentScenarioParametersValues, getActionsImposeesTableId]
  );

  const downloadFileFromStorage = useCallback(
    (selectedGisement) => {
      setIsLoading(true);
      const datasetPartId = getActionImposeesDatasetId(selectedGisement);
      let storageFilePath;
      if (datasetPartId != null) {
        const dataset = datasets?.find((dataset) => dataset.id === datasetPartId);
        storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
      } else {
        const actionImposeesTableId = getActionsImposeesTableId(selectedGisement);
        storageFilePath = `dataset/${datasetId}/${actionImposeesTableId}.csv`;
      }
      if (!storageFilePath) {
        setFileError("Le fichier associé n'existe pas");
        return;
      }
      return WorkspaceService.downloadWorkspaceFile(organizationId, workspaceId, storageFilePath);
    },
    [datasetId, datasets, getActionImposeesDatasetId, getActionsImposeesTableId, organizationId, workspaceId]
  );

  return {
    getActionImposeesDatasetId,
    getActionsImposeesTableId,
    forgeNewActionsImposeesId,
    getActionImposeesId,
    downloadFileFromStorage,
    isLoading,
    setIsLoading,
    fileError,
    setFileError,
  };
};
